<template>
  <div>
    <h2 class="text-left alert" :class="[removed ? 'alert-danger' : 'alert-success']">
      Reproduction Planning
      <a class="btn btn-sm" @click="$emit('onRemoveSection', stepName)">
        <span class="glyphicon" :class="[removed ? 'glyphicon-plus' : 'glyphicon-remove']"></span>
      </a>
    </h2>
    <template v-if="!removed">
      <report-comment field_name="heading_reproduction_planning" :model="model.COMMENTS" :heading="true">
      </report-comment>

      <subsection
        :title="farm.farm_label"
        :step-name="stepName"
        :subsection="`farm_${farm.id}-repro_planning`"
        v-for="farm in model.FARMS"
        :key="'farm_fields_' + farm.id"
      >
        <report-comment
          :field_name="`farm_${farm.id}-heading_farm_label`"
          :model="model.COMMENTS"
          :heading="true"
        ></report-comment>

        <dl class="dl-horizontal">
          <dt>Planned Start of Calving - Herd</dt>
          <dd>{{ model[`farm_${farm.id}-start_calving_herd`] || '-' }}</dd>

          <dt>Planned Start of Calving - Heifers</dt>
          <dd>{{ model[`farm_${farm.id}-start_calving_heifers`] || '-' }}</dd>

          <dt>Planned Start of Mating - Herd</dt>
          <dd>{{ model[`farm_${farm.id}-start_mating_herd`] || '-' }}</dd>

          <dt>Planned Start of Mating - Heifers</dt>
          <dd>{{ model[`farm_${farm.id}-start_mating_heifers`] || '-' }}</dd>
        </dl>
      </subsection>

      <subsection
        title="Dairy Metricheck and Metriclean"
        :step-name="stepName"
        subsection="dairy_metricheck_and_metriclean"
      >
        <report-comment field_name="heading_dairy_metricheck_and_metriclean" :model="model.COMMENTS" :heading="true">
        </report-comment>
      </subsection>

      <subsection
        title="Dairy Metricheck/Metriclean Purchases"
        :step-name="stepName"
        subsection="metricheck_metriclean_purchases"
      >
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Season</th>
              <th>Date</th>
              <th>Product</th>
              <th>Cows</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(rv, index) in model.CHARTS.metri_checked_cleaned_table"
              :key="`mcct_${index}`"
              :class="getRowClass(model.CHARTS.metri_checked_cleaned_table, 'Season', index)"
            >
              <td class="text-left">{{ rv['Season'] }}</td>
              <td class="text-left">{{ rv['Date'] }}</td>
              <td class="text-left">{{ rv['Product'] }}</td>
              <td class="text-left">{{ rv['Cows'] }}</td>
            </tr>
          </tbody>
        </table>
      </subsection>

      <subsection title="Planned Metrichecking" :step-name="stepName" subsection="planned_metrichecking">
        <div v-for="farm in model.FARMS" :key="`planned_metrichecking_${farm.id}`">
          <h4 class="text-left">{{ farm.farm_label }}</h4>
          <report-comment
            :field_name="`farm_${farm.id}-heading_planned_metrichecking`"
            :model="model.COMMENTS"
            :heading="true"
          >
          </report-comment>
          <dl class="dl-horizontal">
            <div class="row">
              <div class="col-md-4">
                <dt>First</dt>
                <dd>{{ model[`farm_${farm.id}-metri_check_1`] || '-' }}</dd>
              </div>

              <div class="col-md-4">
                <dt>Est. Date</dt>
                <dd>{{ model[`farm_${farm.id}-metri_check_date_1`] || '-' }}</dd>
              </div>
            </div>

            <div class="row">
              <div class="col-md-4">
                <dt>Second</dt>
                <dd>{{ model[`farm_${farm.id}-metri_check_2`] || '-' }}</dd>
              </div>

              <div class="col-md-4">
                <dt>Est. Date</dt>
                <dd>{{ model[`farm_${farm.id}-metri_check_date_2`] || '-' }}</dd>
              </div>
            </div>

            <div class="row">
              <div class="col-md-4">
                <dt>Third</dt>
                <dd>{{ model[`farm_${farm.id}-metri_check_3`] || '-' }}</dd>
              </div>

              <div class="col-md-4">
                <dt>Est. Date</dt>
                <dd>{{ model[`farm_${farm.id}-metri_check_date_3`] || '-' }}</dd>
              </div>
            </div>
          </dl>
        </div>

        <div v-for="farm in model.FARMS" :key="`reproduction_programs_data_${farm.id}`">
          <dl class="dl-horizontal">
            <dt>Do you want a reproduction consult or mating planning?</dt>
            <dd>{{ model[`farm_${farm.id}-wants_repro_consult`] || '-' }}</dd>
          </dl>
        </div>
      </subsection>

      <subsection title="Reproduction Programs" :step-name="stepName" subsection="reproduction_programs">
        <div v-for="farm in model.FARMS" :key="`reproduction_programs_${farm.id}`">
          <h4 class="text-left">{{ farm.farm_label }}</h4>
          <report-comment
            :field_name="`farm_${farm.id}-heading_reproduction_programs`"
            :model="model.COMMENTS"
            :heading="true"
          >
          </report-comment>

          <dl class="dl-horizontal">
            <dt>Cow Sync PG?</dt>
            <dd>{{ model[`farm_${farm.id}-cow_sync_pg`] || '-' }}</dd>

            <dt>Heifer Sync CIDR?</dt>
            <dd>{{ model[`farm_${farm.id}-heifer_sync_qmate_cidr`] || '-' }}</dd>

            <dt>Heifer Sync PG?</dt>
            <dd>{{ model[`farm_${farm.id}-heifer_sync_pg`] || '-' }}</dd>

            <dt>Bull fertility testing</dt>
            <dd>{{ model[`farm_${farm.id}-bull_fertility_test`] || '-' }}</dd>
          </dl>
        </div>
      </subsection>

      <subsection title="CIDR Inserts Detail" :step-name="stepName" subsection="cuemate_cidr">
        <report-comment field_name="heading_cuemate_cidr" :model="model.COMMENTS" :heading="true"> </report-comment>
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Season</th>
              <th>Date</th>
              <th>Product</th>
              <th>Cows Treated</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(rv, index) in model.CHARTS.cuemate_cidr_table"
              :key="`cuemate_cidr_table_${index}`"
              :class="getRowClass(model.CHARTS.cuemate_cidr_table, 'Season', index)"
            >
              <td class="text-left">{{ rv['Season'] }}</td>
              <td class="text-left">{{ rv['Date'] }}</td>
              <td class="text-left">{{ rv['Product'] }}</td>
              <td class="text-left">{{ rv['Cows'] }}</td>
            </tr>
          </tbody>
        </table>
      </subsection>

      <subsection title="Non-Cyclers" :step-name="stepName" subsection="non_cyclers">
        <div v-for="farm in model.FARMS" :key="`non_cyclers_${farm.id}`">
          <h4 class="text-left">{{ farm.farm_label }}</h4>
          <report-comment :field_name="`farm_${farm.id}-heading_non_cyclers`" :model="model.COMMENTS" :heading="true">
          </report-comment>

          <dl class="dl-horizontal">
            <div class="row">
              <div class="col-md-4">
                <dt>First</dt>
                <dd>{{ model[`farm_${farm.id}-non_cyclers_1`] || '-' }}</dd>
              </div>

              <div class="col-md-4">
                <dt>Est. Date</dt>
                <dd>{{ model[`farm_${farm.id}-non_cyclers_1_date`] || '-' }}</dd>
              </div>
            </div>

            <div class="row">
              <div class="col-md-4">
                <dt>Second</dt>
                <dd>{{ model[`farm_${farm.id}-non_cyclers_2`] || '-' }}</dd>
              </div>

              <div class="col-md-4">
                <dt>Est. Date</dt>
                <dd>{{ model[`farm_${farm.id}-non_cyclers_2_date`] || '-' }}</dd>
              </div>
            </div>

            <div class="row">
              <div class="col-md-4">
                <dt>Third</dt>
                <dd>{{ model[`farm_${farm.id}-non_cyclers_3`] || '-' }}</dd>
              </div>

              <div class="col-md-4">
                <dt>Est. Date</dt>
                <dd>{{ model[`farm_${farm.id}-non_cyclers_3_date`] || '-' }}</dd>
              </div>
            </div>
          </dl>
        </div>

        <template v-if="model.CHARTS.non_cycling_exam.length > 0">
          <bar :chart-data="nonCyclingExamData" :options="nonCyclingExamOptions" ref="non_cycling_exam"></bar>
        </template>
      </subsection>

      <subsection title="Cow Pregnancy Scan Review" :step-name="stepName" subsection="cow_preg_scan_review">
        <report-comment field_name="heading_cow_pregnancy_scan_review" :model="model.COMMENTS" :heading="true">
        </report-comment>

        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Date</th>
              <th>Product</th>
              <th>Quantity Purchased</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(psr, index) in model.CHARTS.pregnancy_scan_review" :key="`psr_${index}`">
              <td class="text-left">{{ psr['Date'] }}</td>
              <td class="text-left">{{ psr['Product'] }}</td>
              <td class="text-left">{{ psr['Quantity Purchased'] }}</td>
            </tr>
          </tbody>
        </table>
      </subsection>

      <subsection title="Pregnancy Scanning" :step-name="stepName" subsection="preg_scanning">
        <div v-for="farm in model.FARMS" :key="`pregnancy_scanning_${farm.id}`">
          <h4 class="text-left">{{ farm.farm_label }}</h4>
          <report-comment
            :field_name="`farm_${farm.id}-heading_pregnancy_scanning`"
            :model="model.COMMENTS"
            :heading="true"
          >
          </report-comment>

          <dt>Pregnancy Scan with VetSouth?</dt>
          <dd>{{ model[`farm_${farm.id}-preg_scan_with_vs`] ? 'Yes' : 'No' }}</dd>

          <h5>Herd Scan</h5>
          <dl class="dl-horizontal">
            <dt>Early aged</dt>
            <dd>{{ model[`farm_${farm.id}-wants_preg_scan_herd_early_aged`] ? 'Yes' : 'No' }}</dd>

            <dt>Late aged single scan</dt>
            <dd>{{ model[`farm_${farm.id}-wants_preg_scan_herd_late_aged_single`] ? 'Yes' : 'No' }}</dd>

            <dt>Y/N May scan</dt>
            <dd>{{ model[`farm_${farm.id}-wants_preg_scan_herd_yn_may`] ? 'Yes' : 'No' }}</dd>
          </dl>

          <h5>Heifer Scan</h5>
          <dl class="dl-horizontal">
            <dt>Aged scan</dt>
            <dd>{{ model[`farm_${farm.id}-wants_preg_scan_hef_aged`] ? 'Yes' : 'No' }}</dd>

            <dt>Recheck scan</dt>
            <dd>{{ model[`farm_${farm.id}-wants_preg_scan_hef_recheck`] ? 'Yes' : 'No' }}</dd>

            <dt>Y/N May scan</dt>
            <dd>{{ model[`farm_${farm.id}-wants_preg_scan_hef_yn_may`] ? 'Yes' : 'No' }}</dd>
          </dl>
        </div>
      </subsection>

      <subsection title="In-calf Rates" :step-name="stepName" subsection="incalf_rates">
        <div v-for="farm in model.FARMS" :key="`incalf_rates_${farm.id}`">
          <h4 class="text-left">{{ farm.farm_label }}</h4>
          <report-comment :field_name="`farm_${farm.id}-heading_incalf_rates`" :model="model.COMMENTS" :heading="true">
          </report-comment>

          <dl class="dl-horizontal">
            <dt>3 Week ICR (%)</dt>
            <dd>{{ model[`farm_${farm.id}-incalf_three_week`] || '-' }}</dd>

            <dt>6 Week ICR (%)</dt>
            <dd>{{ model[`farm_${farm.id}-incalf_six_week`] || '-' }}</dd>

            <dt>Empty Rate (%)</dt>
            <dd>{{ model[`farm_${farm.id}-incalf_empty`] || '-' }}</dd>

            <dt>Length Mating - AI (days)</dt>
            <dd>{{ model[`farm_${farm.id}-length_mating_ai`] || '-' }}</dd>

            <dt>Length Mating - Bulls (days)</dt>
            <dd>{{ model[`farm_${farm.id}-length_mating_bulls`] || '-' }}</dd>

            <dt>Total Length Mating (days)</dt>
            <dd>{{ model[`farm_${farm.id}-length_mating_total`] || '-' }}</dd>
          </dl>
        </div>
      </subsection>
    </template>
  </div>
</template>

<script>
import mixin from './mixin'
import Bar from '@/components/charts/Bar'

export default {
  name: 'ReproductionPlanning',
  mixins: [mixin],
  components: {
    Bar,
  },
  computed: {
    cuemateCidrData: function () {
      return {
        labels: this.model.CHARTS.cuemate_cidr_table.map((row) => row['Season']).reverse(),
        datasets: [
          {
            label: 'CIDR cows treated as a percent of total cows wintered',
            backgroundColor: this.model.CHARTS.cuemate_cidr_table.map(() => '#3e95cd'),
            data: this.model.CHARTS.cuemate_cidr_table
              .map((row) => parseFloat((row['Percent of TCW'] || '').replace('%', '')))
              .reverse(),
          },
        ],
      }
    },
    cuemateCidrOptions: function () {
      return {
        title: {
          display: true,
          text: 'CIDR Inserts Summary',
          fontSize: 22,
        },
        scales: {
          yAxes: [{ ticks: { beginAtZero: true } }],
        },
      }
    },
    nonCyclingExamData: function () {
      return {
        labels: this.model.CHARTS.non_cycling_exam.map((row) => `${row[0]} ${row[3]}`).reverse(),
        datasets: [
          {
            label: 'Dairy Cow Non-Cycling Exam',
            backgroundColor: this.model.CHARTS.non_cycling_exam.map(() => '#c45850'),
            data: this.model.CHARTS.non_cycling_exam.map((row) => parseFloat(row[4] || '')).reverse(),
          },
        ],
      }
    },
    nonCyclingExamOptions: function () {
      return {
        legend: { display: false },
        title: { display: true, text: 'Dairy Cow Non-Cycling Exam' },
      }
    },
  },
}
</script>
